/**
 * GeoPlaybackHistory -- Flush heatmap for UserDrawer Column 1.
 *
 * Shows an interactive Leaflet map (flush, no padding/radius) with markers for
 * geo-enriched sessions. Marker hover shows a popover with session details.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, Marker, Popup, useMap } from "react-leaflet";
import { BasemapLayer } from "../map/BasemapLayer";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import { Icon } from "../ui/Icon";
import { Spinner } from "../ui/Spinner";
import { Tooltip } from "../ui/Tooltip";
import { cn } from "../../utils/cn";

import type { HistoryPlayEntry, UserWatchHistory } from "../../hooks/useUserProfile";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type GeoPlaybackHistoryProps = {
  history: UserWatchHistory | null;
  historyLoading: boolean;
};

// ─────────────────────────────────────────────────────────────────────────────
// Tile Providers
// ─────────────────────────────────────────────────────────────────────────────

import {
  TILE_PROVIDERS, TILE_KEYS, getStoredTile, storeTile,
  type TileProvider,
} from "../../lib/map/tileProviders";

const TILE_STORAGE_KEY = "wsd_geo_map_tile";
const getInitialTile = (): TileProvider => getStoredTile(TILE_STORAGE_KEY);

// ─────────────────────────────────────────────────────────────────────────────
// Custom Markers
// ─────────────────────────────────────────────────────────────────────────────

const pulsingIcon = L.divIcon({
  className: "",
  html: `<span class="wsd-geo-pulse"></span>`,
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

const dotIcon = L.divIcon({
  className: "",
  html: `<span class="wsd-geo-dot"></span>`,
  iconSize: [10, 10],
  iconAnchor: [5, 5],
});

// ─────────────────────────────────────────────────────────────────────────────
// Map Helpers
// ─────────────────────────────────────────────────────────────────────────────

const MapResizeHandler = () => {
  const map = useMap();

  useEffect(() => {
    const container = map.getContainer();
    if (!container) return;

    const raf = requestAnimationFrame(() => map.invalidateSize());

    const observer = new ResizeObserver(() => {
      map.invalidateSize();
    });
    observer.observe(container);

    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, [map]);

  return null;
};

const FitBoundsController = ({ points }: { points: [number, number][] }) => {
  const map = useMap();
  const fittedRef = useRef(false);

  useEffect(() => {
    if (fittedRef.current || points.length === 0) return;
    fittedRef.current = true;

    map.invalidateSize();

    if (points.length === 1) {
      map.setView(points[0]!, 6, { animate: false });
    } else {
      const bounds = L.latLngBounds(points.map(([lat, lon]) => [lat, lon]));
      map.fitBounds(bounds, { padding: [20, 20], animate: false, maxZoom: 8 });
    }
  }, [map, points]);

  return null;
};

const MapControls = ({
  tile,
  onTileChange,
}: {
  tile: TileProvider;
  onTileChange: (tile: TileProvider) => void;
}) => (
  <div className="absolute top-2 right-2 z-[1000] flex items-center gap-0.5 rounded-lg bg-card/80 backdrop-blur-sm p-1 border border-border">
    {TILE_KEYS.map((key) => (
      <Tooltip key={key} content={TILE_PROVIDERS[key].description ?? `${TILE_PROVIDERS[key].label} map`}>
        <button
          type="button"
          aria-label={`${TILE_PROVIDERS[key].label} map tiles`}
          aria-pressed={tile === key}
          onClick={(e) => {
            e.stopPropagation();
            onTileChange(key);
          }}
          className={cn(
            "flex h-6 w-6 items-center justify-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/70",
            tile === key
              ? "bg-primary/15 text-primary"
              : "text-muted-foreground hover:bg-muted hover:text-foreground",
          )}
        >
          <Icon icon={TILE_PROVIDERS[key].icon} className="h-3 w-3" />
        </button>
      </Tooltip>
    ))}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const getCountryFlagEmoji = (countryCode?: string | null): string => {
  if (!countryCode || countryCode.length !== 2) return "";
  const codePoints = [...countryCode.toUpperCase()].map(
    (char) => 0x1f1e6 - 65 + char.charCodeAt(0)
  );
  return String.fromCodePoint(...codePoints);
};

const formatTimestamp = (unix: number): string => {
  return new Date(unix * 1000).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

const formatLocation = (city: string | null, countryCode: string | null): string => {
  const flag = getCountryFlagEmoji(countryCode);
  const parts = [city, countryCode?.toUpperCase()].filter(Boolean);
  if (parts.length === 0) return "Unknown location";
  return flag ? `${flag} ${parts.join(", ")}` : parts.join(", ");
};

/** Filter and get items with valid coordinates */
const getGeoItems = (items: HistoryPlayEntry[]): HistoryPlayEntry[] =>
  items.filter(
    (item) => item.geoLatitude != null && item.geoLongitude != null
  );

/** Cluster sessions by location (rounded to ~1km precision) */
type GeoCluster = {
  lat: number;
  lon: number;
  sessions: HistoryPlayEntry[];
};

const clusterByLocation = (items: HistoryPlayEntry[]): GeoCluster[] => {
  const map = new Map<string, GeoCluster>();
  for (const item of items) {
    const lat = item.geoLatitude!;
    const lon = item.geoLongitude!;
    const key = `${lat.toFixed(2)},${lon.toFixed(2)}`;
    const existing = map.get(key);
    if (existing) {
      existing.sessions.push(item);
    } else {
      map.set(key, { lat, lon, sessions: [item] });
    }
  }
  return Array.from(map.values());
};

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export const GeoPlaybackHistory = ({
  history,
  historyLoading,
}: GeoPlaybackHistoryProps) => {
  const [tile, setTile] = useState<TileProvider>(getInitialTile);

  const geoItems = useMemo(
    () => (history ? getGeoItems(history.items) : []),
    [history]
  );

  const clusters = useMemo(() => clusterByLocation(geoItems), [geoItems]);

  const mapPoints = useMemo<[number, number][]>(
    () => clusters.map((c) => [c.lat, c.lon]),
    [clusters]
  );

  const latestGeoItem = geoItems.length > 0 ? geoItems[0] : null;


  const handleTileChange = useCallback((key: TileProvider) => {
    setTile(key);
    storeTile(TILE_STORAGE_KEY, key);
  }, []);

  // Loading state
  if (historyLoading && !history) {
    return (
      <div className="flex items-center justify-center py-8 text-muted-foreground">
        <Spinner size="4" className="mr-2" />
        <span className="text-xs">Loading...</span>
      </div>
    );
  }

  return (
    <div>
      {/* Geo Playback Heatmap — flush, no heading */}
      <div className="relative z-0 overflow-hidden" role="region" aria-label="Geo playback heatmap">
          {geoItems.length === 0 ? (
            // Empty state
            <div className="flex flex-col items-center justify-center py-8 px-4 text-center bg-muted/10">
              <Icon icon="lucide:map-pin-off" className="h-6 w-6 text-muted-foreground/40 mb-1.5" />
              <p className="text-[10px] 2xl:text-xs text-muted-foreground">
                No location data in this period
              </p>
            </div>
          ) : (
            <>
              <MapContainer
                center={[20, 0]}
                zoom={2}
                zoomControl={false}
                attributionControl={false}
                scrollWheelZoom={true}
                dragging={true}
                doubleClickZoom={true}
                touchZoom={true}
                keyboard={false}
                className="w-full h-[180px] 2xl:h-[240px]"
              >
                <MapResizeHandler />
                <FitBoundsController points={mapPoints} />
                <BasemapLayer tile={tile} />

                {clusters.map((cluster, idx) => {
                  const isLatest =
                    latestGeoItem != null &&
                    cluster.sessions.some((s) => s.id === latestGeoItem.id);

                  // Get the most recent session in this cluster
                  const latestSession = cluster.sessions.reduce((a, b) =>
                    a.startedAt > b.startedAt ? a : b
                  );

                  return (
                    <Marker
                      key={`${cluster.lat}-${cluster.lon}-${idx}`}
                      position={[cluster.lat, cluster.lon]}
                      icon={isLatest ? pulsingIcon : dotIcon}
                    >
                      <Popup
                        closeButton={false}
                        className="wsd-geo-popup"
                        offset={[0, -4]}
                      >
                        <div className="space-y-1.5 min-w-[160px]">
                          {/* Location */}
                          <div className="flex items-start gap-2">
                            <Icon icon="lucide:map-pin" className="h-3 w-3 text-muted-foreground mt-0.5 shrink-0" />
                            <div>
                              <div className="text-xs 2xl:text-sm font-medium text-foreground">
                                {formatLocation(latestSession.geoCity, latestSession.geoCountryCode)}
                              </div>
                              {cluster.sessions.length > 1 && (
                                <div className="text-[10px] text-muted-foreground">
                                  {cluster.sessions.length} sessions
                                </div>
                              )}
                            </div>
                          </div>

                          {/* IP */}
                          {latestSession.ipAddress && (
                            <div className="flex items-center gap-2">
                              <Icon icon="lucide:globe" className="h-3 w-3 text-muted-foreground shrink-0" />
                              <span className="font-mono text-[10px] text-foreground">{latestSession.ipAddress}</span>
                            </div>
                          )}

                          {/* Device */}
                          <div className="flex items-center gap-2">
                            <Icon icon="lucide:monitor-smartphone" className="h-3 w-3 text-muted-foreground shrink-0" />
                            <span className="text-[10px] text-foreground truncate">{latestSession.deviceName}</span>
                          </div>

                          {/* Timestamp */}
                          <div className="flex items-center gap-2 pt-1 border-t border-border/30">
                            <Icon icon="lucide:clock" className="h-3 w-3 text-muted-foreground shrink-0" />
                            <span className="text-[9px] text-muted-foreground">{formatTimestamp(latestSession.startedAt)}</span>
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  );
                })}
              </MapContainer>

              {/* Tile controls overlay */}
              <MapControls tile={tile} onTileChange={handleTileChange} />

              {/* Click hint */}
              <div className="absolute bottom-1.5 left-1.5 z-[1000] text-[9px] text-muted-foreground bg-card/80 border border-border backdrop-blur-sm px-1.5 py-0.5 rounded">
                Click markers for details
              </div>
            </>
          )}
        </div>

      {/* Inline CSS for markers and popup */}
      <style>{`
        .wsd-geo-pulse {
          display: block;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: rgb(59 130 246);
          box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.5);
          animation: wsd-pulse 2s ease-out infinite;
          margin: 2px;
        }
        .wsd-geo-dot {
          display: block;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: rgba(59, 130, 246, 0.7);
          border: 1.5px solid rgba(59, 130, 246, 0.4);
          margin: 1px;
        }
        @keyframes wsd-pulse {
          0% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.5); }
          70% { box-shadow: 0 0 0 10px rgba(59, 130, 246, 0); }
          100% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0); }
        }
        .wsd-geo-popup .leaflet-popup-content-wrapper {
          background: rgb(var(--card) / 0.95);
          color: rgb(var(--card-foreground));
          border: 1px solid rgb(var(--border) / 0.6);
          border-radius: 8px;
          box-shadow: var(--shadow-soft);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          padding: 0;
        }
        .wsd-geo-popup .leaflet-popup-content {
          margin: 10px 12px;
          font-size: 12px;
        }
        .wsd-geo-popup .leaflet-popup-tip {
          background: rgb(var(--card) / 0.95);
          border: 1px solid rgb(var(--border) / 0.6);
          border-top: none;
          border-left: none;
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
        }
      `}</style>
    </div>
  );
};
